class SearchForm extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');

    if (this.input) {
      this.input.form.addEventListener('reset', this.onFormReset.bind(this));
      // Spirit Goth: bound to a name unique to this class - see the long
      // comment below onSgWildcardSubmit for why that matters.
      this.input.form.addEventListener('submit', this.onSgWildcardSubmit.bind(this));
      this.input.addEventListener(
        'input',
        debounce((event) => {
          this.onChange(event);
        }, 300).bind(this)
      );
    }
  }

  toggleResetButton() {
    const resetIsHidden = this.resetButton.classList.contains('hidden');
    if (this.input.value.length > 0 && resetIsHidden) {
      this.resetButton.classList.remove('hidden');
    } else if (this.input.value.length === 0 && !resetIsHidden) {
      this.resetButton.classList.add('hidden');
    }
  }

  onChange() {
    this.toggleResetButton();
  }

  shouldResetForm() {
    return !document.querySelector('[aria-selected="true"] a');
  }

  onFormReset(event) {
    // Prevent default so the form reset doesn't set the value gotten from the url on page load
    event.preventDefault();
    // Don't reset if the user has selected an element on the predictive search dropdown
    if (this.shouldResetForm()) {
      this.input.value = '';
      this.input.focus();
      this.toggleResetButton();
    }
  }

  // Spirit Goth: Shopify's search matches whole words by default, so "cool"
  // never surfaces "Coolhand" even though it's a clean substring match a
  // visitor would expect to work. Appending an asterisk to a word tells
  // Shopify's search syntax to match anything that starts with it.
  //
  // This is named onSgWildcardSubmit rather than onFormSubmit deliberately.
  // predictive-search.js defines `class PredictiveSearch extends SearchForm`
  // with its OWN method also called onFormSubmit. Because of how JavaScript
  // resolves methods on subclasses, `this.onFormSubmit` referenced from
  // *this* class's constructor - on a PredictiveSearch instance - resolves
  // to PredictiveSearch's version, not this one, silently discarding the
  // wildcard logic entirely. That's exactly why this worked on the search
  // results page (which wraps the input in an extra <main-search> element
  // with no such name collision) but not from the header, which only uses
  // <predictive-search>. A name nothing else defines can't be shadowed this
  // way, regardless of what subclasses do.
  //
  // The wildcarded value is submitted through a separate hidden field
  // rather than by rewriting the visible input's value - an earlier version
  // did that, and it caused a one-frame flash of "cool*" in the search box
  // right as the page navigated away. The visible field is excluded from
  // submission (name cleared) so only the hidden field's wildcarded value
  // reaches Shopify; what the visitor sees never changes. form.submit()
  // bypasses event listeners entirely, so there's no risk of looping back
  // into this one.
  //
  // Skipped if the visitor already typed a wildcard, quotes, or a field
  // query like vendor:coolhand - those are intentional advanced searches
  // and should reach Shopify exactly as typed.
  onSgWildcardSubmit(event) {
    if (!this.input) return;
    const value = this.input.value.trim();
    if (!value || /[*":]/.test(value)) return;

    event.preventDefault();

    const wildcarded = value
      .split(/\s+/)
      .map((word) => `${word}*`)
      .join(' ');

    const hiddenQuery = document.createElement('input');
    hiddenQuery.type = 'hidden';
    hiddenQuery.name = this.input.name;
    hiddenQuery.value = wildcarded;

    const originalName = this.input.name;
    this.input.name = '';
    this.input.form.appendChild(hiddenQuery);
    this.input.form.submit();
    // Restore in case submission is somehow cancelled (e.g. a browser extension)
    this.input.name = originalName;
  }
}

customElements.define('search-form', SearchForm);
